import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";
import { useForm } from "@tanstack/react-form";
import React, { Suspense, useEffect } from "react";

import HeadingLayout from "src/components/base/heading-layout";
import { Input, InputGroup } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import {
    ArrowPathRoundedSquareIcon,
    CheckCircleIcon,
    ClipboardDocumentIcon,
    ClockIcon,
    EllipsisVerticalIcon,
    MagnifyingGlassIcon,
    RectangleGroupIcon,
    XCircleIcon,
} from "@heroicons/react/20/solid";
import { TableHeader } from "src/components/base/table";
import { Badge } from "src/components/base/badge";

import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TFunction } from "i18next";

import { FullHeadscalePreAuthKey } from "src/api/generated/frontend_internal_v1";
import WS from "src/api/ws";
import ExpirePreAutKeyDialog from "src/components/dialogs/expire-pre-auth-key";
import { TagBadge } from "src/components/badges/tag";
import { TenantBadge } from "src/components/badges/tenant";
import { EmptyState } from "src/components/base/empty-state";
import { KeyIcon } from "@heroicons/react/24/solid";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { DataTable, useDataTable } from "src/components/data-table";
import { ToggleButton } from "src/components/toggle-button";
import { HighlightTerm } from "src/components/highlight-term";
import HIGHLIGHT_CONTEXT from "src/context/highlight";
import { createColumnHelper } from "@tanstack/react-table";
import { CheckboxFilterTile } from "src/components/base/filter-tile";
import { UUID } from "src/api/api_common";

/**
 * The properties for {@link PreAuthKeysOverview }
 */
export type PreAuthKeysOverviewProps = {};

/**
 * The flag filter pushed into the flags column
 */
type FlagsFilter = {
    /** Only show ephemeral keys */
    ephemeral?: boolean;
    /** Only show reusable keys */
    reusable?: boolean;
    /** Only show used keys */
    used?: boolean;
};

/**
 * Checks whether a pre-auth key is expired
 *
 * @param expiration the key's expiration date (if any)
 * @returns true if the key is expired
 */
function isExpired(expiration: string | null | undefined): boolean {
    return !!expiration && new Date(expiration).getTime() <= Date.now();
}

/**
 * An overview over all pre-auth keys
 */
function PreAuthKeysOverview(props: PreAuthKeysOverviewProps) {
    const [t] = useTranslation("admin-preauth-overview");
    const [tg] = useTranslation();
    const [openExpireKey, setOpenExpireKey] = React.useState<FullHeadscalePreAuthKey>();
    const router = useRouter();
    const navigate = Route.useNavigate();
    const { search, used, reusable, ephemeral, expired, page, tenant } = Route.useSearch();

    const { keys, tenants } = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.KeysChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    const columnHelper = createColumnHelper<FullHeadscalePreAuthKey>();
    const columns = [
        columnHelper.accessor((key) => key.tenant.name, {
            id: "tenant",
            filterFn: (row, _columnId, filterValue: string) => row.original.tenant.uuid === filterValue,
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.tenant")}
                </TableHeader>
            ),
            cell: (info) => <TenantBadge tenant={info.row.original.tenant} />,
        }),

        columnHelper.accessor((key) => key.key, {
            id: "key",
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.key")}
                </TableHeader>
            ),
            cell: (info) => (
                <div className={"flex items-center gap-1"}>
                    <Button
                        plain={true}
                        onClick={async () => {
                            await navigator.clipboard.writeText(info.row.original.key);
                            notify.success(tg("toast.copied-to-clipboard"));
                        }}
                    >
                        <span className={"w-[10ch] overflow-hidden text-ellipsis"}>{info.row.original.key}</span>
                        <ClipboardDocumentIcon />
                    </Button>
                </div>
            ),
        }),

        columnHelper.accessor((key) => key.expiration ?? "", {
            id: "expires",
            enableGlobalFilter: false,
            // The filter hides expired keys (set when the "show expired" checkbox is unchecked)
            filterFn: (row) => !isExpired(row.original.expiration),
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.expires")}
                </TableHeader>
            ),
            cell: (info) => {
                const expiration = info.row.original.expiration;
                return expiration && displayExpiry(new Date(expiration), t);
            },
        }),
        columnHelper.accessor((key) => `${key.ephemeral} ${key.reusable} ${key.used}`, {
            id: "flags",
            enableSorting: false,
            enableGlobalFilter: false,
            filterFn: (row, _columnId, filterValue: FlagsFilter) =>
                (!filterValue.ephemeral || row.original.ephemeral) &&
                (!filterValue.reusable || row.original.reusable) &&
                (!filterValue.used || row.original.used),
            header: () => <TableHeader></TableHeader>,
            cell: (info) => {
                const key = info.row.original;
                return (
                    <div className={"flex gap-6"}>
                        {key.ephemeral ? (
                            <ClockIcon
                                title={t("label.ephemeral")}
                                className={"size-4 fill-zinc-900 dark:fill-white"}
                            />
                        ) : (
                            <ClockIcon
                                title={t("label.ephemeral")}
                                className={"size-4 fill-zinc-300 dark:fill-zinc-800"}
                            />
                        )}
                        {key.reusable ? (
                            <ArrowPathRoundedSquareIcon
                                title={t("label.reusable")}
                                className={"size-4 fill-zinc-900 dark:fill-white"}
                            />
                        ) : (
                            <ArrowPathRoundedSquareIcon
                                title={t("label.reusable")}
                                className={"size-4 fill-zinc-300 dark:fill-zinc-800"}
                            />
                        )}
                        {key.used ? (
                            <CheckCircleIcon
                                title={t("label.used")}
                                className={"size-4 text-zinc-900 dark:text-white"}
                            />
                        ) : (
                            <CheckCircleIcon
                                title={t("label.used")}
                                className={"size-4 text-zinc-300 dark:text-zinc-800"}
                            />
                        )}
                    </div>
                );
            },
        }),

        columnHelper.accessor((key) => key.tags.map((tag) => tag.name).join(" "), {
            id: "tags",
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.tags")}
                </TableHeader>
            ),
            cell: (info) => (
                <div className={"flex flex-wrap gap-1"}>
                    {info.row.original.tags.map((tag) => (
                        <TagBadge key={tag.uuid} tag={tag} />
                    ))}
                </div>
            ),
        }),

        columnHelper.accessor((key) => key.description, {
            id: "description",
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.description")}
                </TableHeader>
            ),
            cell: (info) => (
                <div className={"w-[45ch] text-wrap break-all whitespace-pre-wrap"}>
                    <HighlightTerm value={info.row.original.description} />
                </div>
            ),
        }),

        columnHelper.display({
            id: "actions",
            header: () => (
                <TableHeader className={"w-0"}>
                    <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                </TableHeader>
            ),
            size: 0,
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownButton plain aria-label={tg("accessibility.actions")}>
                        <EllipsisVerticalIcon />
                    </DropdownButton>
                    <DropdownMenu anchor={"bottom end"}>
                        <DropdownSection>
                            <DropdownHeading>{tg("heading.danger-zone")}</DropdownHeading>
                            <DropdownItem onClick={() => setOpenExpireKey(row.original)}>
                                <DropdownLabel>{t("button.set-expired")}</DropdownLabel>
                            </DropdownItem>
                        </DropdownSection>
                    </DropdownMenu>
                </Dropdown>
            ),
            enableHiding: false,
        }),
    ];

    const { table, isGrouped, setGrouping } = useDataTable({
        data: keys.list,
        columns,
        groupBy: ["tenant"],
        pageIndex: page - 1,
        onPageChange: (pageIndex) => navigate({ search: (prev) => ({ ...prev, page: pageIndex + 1 }), replace: true }),
    });

    // Filtering is fully client-side: the `search` and flag URL params are
    // the source of truth, and these effects push them into the table. The
    // loader has no `loaderDeps`, so updating them never re-fetches the data.
    // Page resets happen at the interaction sites (navigating with `page: 1`)
    // instead of here, so a deep-linked page isn't wiped on mount.
    useEffect(() => {
        table.getColumn("tenant")?.setFilterValue(tenant === "all" ? undefined : tenant);
    }, [tenant]);

    useEffect(() => {
        table
            .getColumn("flags")
            ?.setFilterValue(
                ephemeral || reusable || used ? ({ ephemeral, reusable, used } satisfies FlagsFilter) : undefined,
            );
    }, [ephemeral, reusable, used]);

    useEffect(() => {
        table.getColumn("expires")?.setFilterValue(expired ? undefined : true);
    }, [expired]);

    useEffect(() => {
        table.setGlobalFilter(search);
    }, [search]);

    const tenantItems = !tenant || tenant === "all" ? keys.list : keys.list.filter((k) => k.tenant.uuid === tenant);
    const chipEphemeral = tenantItems.filter((k) => k.ephemeral).length;
    const chipReusable = tenantItems.filter((k) => k.reusable).length;
    const chipUsed = tenantItems.filter((k) => k.used).length;
    const chipExpired = tenantItems.filter((k) => isExpired(k.expiration)).length;

    return (
        <HeadingLayout
            heading={t("heading.preauth-overview")}
            headingDescription={t("description.preauth")}
            headingChildren={
                <PrimaryButton href={"/a/devices/create-server"}>{t("button.create-preauthkey")}</PrimaryButton>
            }
        >
            {/* Filter strip — unlike the devices overview these chips behave like
                checkboxes: each flag can be toggled independently. */}
            <div className={"grid grid-cols-2 gap-3 sm:grid-cols-4"}>
                <CheckboxFilterTile
                    checked={ephemeral ?? false}
                    icon={<ClockIcon className={"size-5"} />}
                    label={t("label.ephemeral")}
                    count={chipEphemeral}
                    onChange={async (x) => {
                        await navigate({
                            search: (prev) => ({ ...prev, ephemeral: x || undefined, page: 1 }),
                            replace: true,
                        });
                    }}
                />
                <CheckboxFilterTile
                    checked={reusable ?? false}
                    icon={<ArrowPathRoundedSquareIcon className={"size-5"} />}
                    label={t("label.reusable")}
                    count={chipReusable}
                    onChange={async (x) => {
                        await navigate({
                            search: (prev) => ({ ...prev, reusable: x || undefined, page: 1 }),
                            replace: true,
                        });
                    }}
                />
                <CheckboxFilterTile
                    checked={used ?? false}
                    icon={<CheckCircleIcon className={"size-5"} />}
                    label={t("label.used")}
                    count={chipUsed}
                    onChange={async (x) => {
                        await navigate({
                            search: (prev) => ({ ...prev, used: x || undefined, page: 1 }),
                            replace: true,
                        });
                    }}
                />
                <CheckboxFilterTile
                    checked={expired ?? false}
                    icon={<XCircleIcon className={"size-5"} />}
                    label={t("label.show-expired")}
                    count={chipExpired}
                    onChange={async (x) => {
                        await navigate({
                            search: (prev) => ({ ...prev, expired: x || undefined, page: 1 }),
                            replace: true,
                        });
                    }}
                />
            </div>

            <div className={"flex flex-col gap-3 sm:flex-row sm:items-center"}>
                <div className={"flex-1"}>
                    <form.Field
                        name={"search"}
                        validators={{
                            onChangeAsync: async ({ fieldApi }) => {
                                await navigate({
                                    search: (prev) => ({ ...prev, search: fieldApi.state.value, page: 1 }),
                                    replace: true,
                                });
                            },
                            onChangeAsyncDebounceMs: 500,
                        }}
                    >
                        {(fieldApi) => (
                            <InputGroup>
                                <MagnifyingGlassIcon data-slot="icon" />
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    placeholder={t("label.filter")}
                                    aria-label={t("label.filter")}
                                />
                            </InputGroup>
                        )}
                    </form.Field>
                </div>
                <div className={"sm:w-56"}>
                    <Listbox
                        aria-label={t("label.filter-by-tenant")}
                        value={tenant ?? "all"}
                        onChange={async (x: string) =>
                            await navigate({
                                search: (prev) => ({ ...prev, tenant: x === "all" ? undefined : x, page: 1 }),
                                replace: true,
                            })
                        }
                    >
                        <ListboxOption value={"all"}>
                            <ListboxLabel>{t("label.all-tenants")}</ListboxLabel>
                        </ListboxOption>
                        {tenants.list.map((tenant) => (
                            <ListboxOption key={tenant.uuid} value={tenant.uuid}>
                                <ListboxLabel>{tenant.name}</ListboxLabel>
                            </ListboxOption>
                        ))}
                    </Listbox>
                </div>
                <ToggleButton active={isGrouped} onClick={() => setGrouping(!isGrouped)}>
                    <RectangleGroupIcon />
                    {t("label.group-by-tenant")}
                </ToggleButton>
            </div>

            {table.getFilteredRowModel().rows.length === 0 ? (
                <EmptyState
                    icon={<KeyIcon />}
                    title={tg("empty.no-results-title")}
                    description={tg("empty.no-results-description")}
                    action={
                        <PrimaryButton href={"/a/devices/create-server"}>{t("button.create-preauthkey")}</PrimaryButton>
                    }
                />
            ) : (
                <HIGHLIGHT_CONTEXT.Provider value={{ searchTerm: table.getState().globalFilter }}>
                    <DataTable table={table} groupBy={isGrouped ? ["tenant"] : undefined} />
                </HIGHLIGHT_CONTEXT.Provider>
            )}

            <Suspense>
                <ExpirePreAutKeyDialog
                    preAuthKey={openExpireKey}
                    isOpen={!!openExpireKey}
                    onClose={() => setOpenExpireKey(undefined)}
                    onExpire={() => {
                        router.invalidate({ sync: true });
                        setOpenExpireKey(undefined);
                    }}
                />
            </Suspense>
        </HeadingLayout>
    );
}

/**
 * Display the time until expiration in different ways
 *
 * @param expiry The deadline to display
 * @param t for Translation
 *
 * @returns The rendered date
 */
function displayExpiry(expiry: Date, t: TFunction) {
    const now = new Date();
    dayjs.extend(relativeTime);
    const date = dayjs(expiry).fromNow();

    if (now.getTime() - expiry.getTime() > 0) {
        return <Badge color={"zinc"}>{t("label.expired").toLowerCase()}</Badge>;
    } else {
        return <Badge color={"blue"}>{date}</Badge>;
    }
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PreAuthKeysOverviewSearch = {
    /** The page to display */
    page: number;
    /** The search term that should be used to search trough descriptions */
    search: string;
    /** Filter by their ephemeral field */
    ephemeral?: boolean;
    /** Filter by their reusable field */
    reusable?: boolean;
    /** Filter by their reusable field */
    used?: boolean;
    /** Filter to show expired keys or not*/
    expired?: boolean;
    /** Filter by tenant */
    tenant?: "all" | UUID;
};

export const Route = createFileRoute("/_admin/a/preauthkeys/")({
    component: PreAuthKeysOverview,
    validateSearch: (search: Record<string, unknown>): PreAuthKeysOverviewSearch => {
        const page = Number(search.page ?? 1);
        return {
            page: Number.isInteger(page) && page > 0 ? page : 1,
            search: typeof search.search === "string" ? search.search : "",
            ephemeral: typeof search.ephemeral === "boolean" ? search.ephemeral : undefined,
            expired: typeof search.expired === "boolean" ? search.expired : undefined,
            reusable: typeof search.reusable === "boolean" ? search.reusable : undefined,
            used: typeof search.used === "boolean" ? search.used : undefined,
            tenant: typeof search.tenant === "string" ? search.tenant : "all",
        };
    },
    loader: async () => {
        const [keys, tenants] = await Promise.all([ApiInternal.preAuthKeys.getAll(), ApiInternal.tenants.getAll()]);
        return { keys, tenants };
    },
});
